import { Capacitor } from '@capacitor/core';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { datadogRum } from '@datadog/browser-rum';
import { PlatformConfig } from '../config';

export const PREVENT_LIVE_UPDATE_RELOAD_KEY = 'preventLiveUpdateReload';

export const isLiveUpdateEnabled = (): boolean => Capacitor.isNativePlatform() && !PlatformConfig.isDev;

let releaseGate: () => void;
const gate = new Promise<void>((resolve) => {
  releaseGate = resolve;
});

let splashReleased = false;
let pendingLateUpdate = false;
let lateUpdateListener: (() => void) | null = null;
let syncStartedAt: number | null = null;

const notifyLateUpdate = (): void => {
  pendingLateUpdate = true;
  lateUpdateListener?.();
};

export const setLateUpdateListener = (listener: (() => void) | null): void => {
  lateUpdateListener = listener;
  if (listener && pendingLateUpdate) {
    listener();
  }
};

export const waitForInitialLiveUpdateGate = async (timeoutMs: number): Promise<void> => {
  const elapsedSinceSyncStart = syncStartedAt === null ? 0 : Date.now() - syncStartedAt;
  const remainingMs = Math.max(0, timeoutMs - elapsedSinceSyncStart);
  await Promise.race([gate, new Promise<void>((resolve) => setTimeout(resolve, remainingMs))]);
  splashReleased = true;
};

export const applyPendingUpdate = async (): Promise<void> => {
  try {
    await LiveUpdate.reload();
  } catch (error) {
    datadogRum.addError('Live update reload failed', { error });
  }
};

export const startInitialLiveUpdateSync = async (): Promise<void> => {
  syncStartedAt = Date.now();
  if (!isLiveUpdateEnabled()) {
    releaseGate();
    return;
  }
  try {
    await LiveUpdate.ready();
    if (localStorage[PREVENT_LIVE_UPDATE_RELOAD_KEY] !== 'true') {
      const { nextBundleId } = await LiveUpdate.sync();
      localStorage.shouldReloadApp = nextBundleId !== null;
      if (nextBundleId !== null) {
        if (splashReleased) {
          notifyLateUpdate();
        } else {
          await LiveUpdate.reload();
          return;
        }
      }
    }
  } catch (error) {
    datadogRum.addError('Live update initial sync failed', { error });
  }
  releaseGate();
};
